import React, { useState, useCallback, useEffect, useRef } from 'react';
import debounce from 'lodash.debounce';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import { fetchAuthSession } from 'aws-amplify/auth';

const ProductSearch = ({ setSearchLookup }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [visibleCount, setVisibleCount] = useState(3);
  const [loading, setLoading] = useState(false);
  const tokenRef = useRef(null);

  useEffect(() => {
    const loadToken = async () => {
      try {
        const session = await fetchAuthSession({ forceRefresh: true });
        if (typeof session.tokens?.idToken === 'string') tokenRef.current = session.tokens.idToken;
        else if (session.tokens?.idToken?.jwtToken) tokenRef.current = session.tokens.idToken.jwtToken;
        else if (session.tokens?.idToken?.toString) tokenRef.current = session.tokens.idToken.toString();
      } catch { /* unauthenticated */ }
    };
    loadToken();
  }, []);

  const fetchResults = useCallback(
    debounce(async (searchTerm) => {
      if (!searchTerm.trim()) {
        setResults([]);
        setSearchLookup({});
        return;
      }
      setLoading(true);
      try {
        const headers = tokenRef.current ? { Authorization: `Bearer ${tokenRef.current}` } : {};
        const res = await fetch(`/api/products/search?q=${encodeURIComponent(searchTerm)}`, { headers });
        const data = await res.json();
        const wrapper = typeof data.body === 'string' ? JSON.parse(data.body) : data;
        const products = wrapper.products || (Array.isArray(wrapper) ? wrapper : []);

        const normalized = products.map(p => ({
          ...p,
          id: p.prod_id || p.id,
          image: p.image_url
            ? `https://cosmelytix-products-imgs.s3.us-east-1.amazonaws.com/${p.image_url}`
            : p.image,
        }));

        setResults(normalized);
        setVisibleCount(3);

        const lookup = {};
        normalized.forEach(p => { lookup[`search-${p.id}`] = p; });
        setSearchLookup(lookup);
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setLoading(false);
      }
    }, 400),
    [setSearchLookup]
  );

  const handleChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    fetchResults(value);
  };

  return (
    <div className="mb-6 bg-[#E9A969] rounded-lg p-4 shadow-sm">
      <input
        type="text"
        value={query}
        onChange={handleChange}
        placeholder="Search for a product..."
        className="w-full border border-[#5F40A1]/50 bg-white/50 rounded px-4 py-2"
      />

      {loading && (
        <div className="mt-3 space-y-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-center gap-3 p-2 bg-white/60 rounded animate-pulse">
              <div className="w-10 h-10 bg-gray-200 rounded" />
              <div className="flex-1 space-y-1">
                <div className="h-4 bg-gray-200 rounded w-2/3" />
                <div className="h-3 bg-gray-200 rounded w-1/3" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && results.length > 0 && (
        <Droppable droppableId="searchResults" isDropDisabled={false}>
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps} className="mt-4 space-y-3">
              {results.slice(0, visibleCount).map((p, index) => (
                <Draggable key={`search-${p.id}`} draggableId={`search-${p.id}`} index={index}>
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className="flex items-center justify-between p-2 bg-white rounded shadow-sm border border-gray-200"
                    >
                      <div className="flex items-center gap-3">
                        {p.image && (
                          <img src={p.image} alt={p.name} className="w-10 h-10 object-contain rounded" />
                        )}
                        <div>
                          <a
                            href={`/products/${p.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#5F40A1] font-medium hover:underline"
                          >
                            {p.name || p.title}
                          </a>
                          {p.brand && <div className="text-xs text-[#5F40A1]/50">{p.brand}</div>}
                        </div>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
              {visibleCount < results.length && (
                <div className="text-center mt-4">
                  <button
                    onClick={() => setVisibleCount(prev => prev + 3)}
                    className="bg-[#5F40A1] text-[#E9A969] px-6 py-2 rounded-full hover:bg-[#4709CB] font-semibold shadow-md"
                  >
                    load more
                  </button>
                </div>
              )}
            </div>
          )}
        </Droppable>
      )}
    </div>
  );
};

export default ProductSearch;
